import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Toggle,
  Button,
  Alert,
  Spinner,
  ColumnLayout,
  Box,
  Badge,
} from '@cloudscape-design/components';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import axios from 'axios';

export default function LlmConfig() {
  const { user, isSuperAdmin } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const token = user?.access_token || user?.token;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [modelName, setModelName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [hasApiKey, setHasApiKey] = useState(false);
  const [temperature, setTemperature] = useState('0.7');
  const [maxTokens, setMaxTokens] = useState('1024');

  const fetchConfig = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/llm-config/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        const d = res.data.data;
        setConfig(d);
        setEnabled(d.enabled || false);
        setModelName(d.model_name || '');
        setBaseUrl(d.base_url || '');
        setHasApiKey(d.has_api_key || false);
        setTemperature(String(d.temperature ?? 0.7));
        setMaxTokens(String(d.max_tokens ?? 1024));
      }
    } catch (err) {
      notifyError('Failed to load LLM configuration');
    } finally {
      setLoading(false);
    }
  }, [token, notifyError]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchConfig();
    }
  }, [isSuperAdmin, fetchConfig]);

  const handleSave = async () => {
    if (!modelName.trim()) {
      notifyError('Model name is required');
      return;
    }
    if (!baseUrl.trim()) {
      notifyError('Base URL is required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        enabled,
        model_name: modelName.trim(),
        base_url: baseUrl.trim(),
        temperature: parseFloat(temperature) || 0.7,
        max_tokens: parseInt(maxTokens, 10) || 1024,
      };
      if (apiKey) {
        body.api_key = apiKey;
      }
      const res = await axios.put('/api/admin/llm-config/update/', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        const d = res.data.data;
        setConfig(d);
        setEnabled(d.enabled);
        setModelName(d.model_name);
        setBaseUrl(d.base_url);
        setHasApiKey(d.has_api_key);
        setApiKey('');
        notifySuccess('LLM configuration saved successfully');
      }
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to save LLM configuration');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <AppLayoutWrapper
        header={{ title: 'LLM Configuration' }}
        breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/llm-config' }, { text: 'LLM Configuration' }]}
      >
        <Alert type="error" header="Access Denied">
          You need super admin privileges to access this page.
        </Alert>
      </AppLayoutWrapper>
    );
  }

  return (
    <AppLayoutWrapper
      header={{
        title: 'Default LLM Configuration',
        description: 'Configure the default LLM model, endpoint, and API key used when deploying agents.',
        actions: (
          <Button variant="primary" onClick={handleSave} loading={saving}>
            Save
          </Button>
        ),
      }}
      breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/llm-config' }, { text: 'LLM Configuration' }]}
    >
      <SpaceBetween size="l">
        {loading && (
          <Box textAlign="center">
            <Spinner size="large" />
          </Box>
        )}

        {!loading && (
          <>
            <Alert
              type="info"
              header="How this works"
            >
              When enabled, all agent deployments (create and update) will use this LLM configuration
              instead of the default builtin model. The API key is encrypted at rest and never exposed
              in API responses.
            </Alert>

            <Container>
              <Form>
                <SpaceBetween size="l">
                  <FormField
                    label="Enable custom LLM"
                    description="When enabled, agent payloads will override the builtin LLM with this configuration."
                  >
                    <Toggle
                      checked={enabled}
                      onChange={({ detail }) => setEnabled(detail.checked)}
                    >
                      {enabled ? 'Enabled' : 'Disabled'}
                    </Toggle>
                  </FormField>

                  <ColumnLayout columns={2}>
                    <FormField
                      label="Model name"
                      description="e.g. gpt-4o, claude-3-sonnet, llama-3.1-70b"
                      errorText={!modelName.trim() ? 'Required' : undefined}
                    >
                      <Input
                        value={modelName}
                        onChange={({ detail }) => setModelName(detail.value)}
                        placeholder="gpt-4o"
                      />
                    </FormField>

                    <FormField
                      label="Base URL"
                      description="LLM API endpoint URL"
                      errorText={!baseUrl.trim() ? 'Required' : undefined}
                    >
                      <Input
                        value={baseUrl}
                        onChange={({ detail }) => setBaseUrl(detail.value)}
                        placeholder="https://api.openai.com/v1"
                      />
                    </FormField>
                  </ColumnLayout>

                  <FormField
                    label="API Key"
                    description={
                      hasApiKey
                        ? 'An API key is already set. Enter a new value to replace it.'
                        : 'Enter the API key for the LLM provider.'
                    }
                  >
                    <Input
                      type="password"
                      value={apiKey}
                      onChange={({ detail }) => setApiKey(detail.value)}
                      placeholder={hasApiKey ? '••••••••••••••••' : 'sk-...'}
                    />
                  </FormField>

                  <ColumnLayout columns={2}>
                    <FormField
                      label="Temperature"
                      description="0.0 = deterministic, 1.0 = creative (default: 0.7)"
                    >
                      <Input
                        value={temperature}
                        onChange={({ detail }) => setTemperature(detail.value)}
                        placeholder="0.7"
                      />
                    </FormField>

                    <FormField
                      label="Max tokens"
                      description="Maximum response tokens (default: 1024)"
                    >
                      <Input
                        value={maxTokens}
                        onChange={({ detail }) => setMaxTokens(detail.value)}
                        placeholder="1024"
                      />
                    </FormField>
                  </ColumnLayout>

                  {config?.updated_at && (
                    <Box color="text-body-secondary" fontSize="body-s">
                      Last updated: {new Date(config.updated_at).toLocaleString()}
                    </Box>
                  )}

                  {enabled && modelName && baseUrl && (
                    <Alert type="success" header="Configuration active">
                      Agents deployed from now on will use <strong>{modelName}</strong> at{' '}
                      <strong>{baseUrl}</strong>.
                    </Alert>
                  )}
                </SpaceBetween>
              </Form>
            </Container>
          </>
        )}
      </SpaceBetween>
    </AppLayoutWrapper>
  );
}
